import { Stack } from 'component-library';
import { useEffect } from 'react';

import { DeforestationCategory, IndicatorName, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { DeforestationHistoricalChart } from '@/components/Charts/DeforestationHistorical';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { GraphFact } from '@/components/Charts/types';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useSetIndicatorHasData } from '@/components/IndicatorCard/IndicatorContextProvider';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useScaledCustomBenchmark } from '@/hooks/useScaledCustomBenchmark';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { NoData } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/NoData';

export const DeforestationGraphTile = ({
  plotGroupId,
  graphType,
  selectedBenchmark,
  ...props
}: Partial<GraphTileProps> & { selectedBenchmark?: string | null }) => {
  const { setIndicatorGraphData } = useSetIndicatorHasData();

  const { [IndicatorName.deforestation_total_historic_graph]: deforestationHistoric } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(
      IndicatorName.deforestation_total_historic_graph,
      null,
      undefined,
      plotGroupId,
    );

  const { [IndicatorName.deforestation_total_benchmark_graph]: deforestationBenchmark } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(
      IndicatorName.deforestation_total_benchmark_graph,
      null,
      undefined,
      plotGroupId,
    );

  const organizationId = useOrganizationId();

  const indicator = GraphTileType.historic_deforestation;
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);

  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmark);

  const { analysisType } = useNCCardContext();
  const isTotal = analysisType === 'total';
  const displayCustomBenchmark = useScaledCustomBenchmark(activeCustomBenchmark, isTotal);

  const noChartData = !deforestationHistoric?.value?.length;

  useEffect(() => {
    setIndicatorGraphData({ graphData: !noChartData });
  }, [noChartData, setIndicatorGraphData]);

  if (noChartData) {
    return <NoData />;
  }

  const chartData = [...(deforestationHistoric?.value ?? []), ...(deforestationBenchmark?.value ?? [])];

  return (
    <Stack center data-testid='deforestation-historic-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        <ItemizedGraphTypeRenderer
          graphType={graphType}
          indicator={GraphTileType.historic_deforestation}
          plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
          plotGroupId={plotGroupId ?? undefined}
          defaultGraphType={<DeforestationHistoricalChart data={chartData} customBenchmark={displayCustomBenchmark} />}
          labels={{
            [PlotReportFactLabelNameEnum.deforestation]: DeforestationCategory.forest_loss,
          }}
        />
      </Stack>
    </Stack>
  );
};
